import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Sparkles } from 'lucide-react';

const ThemeToggle = ({ theme, toggleTheme, themeStyles }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const [showOverlay, setShowOverlay] = useState(false);

  const handleThemeToggle = (e) => {
    // Get click position for ripple effect
    const rect = e.currentTarget.getBoundingClientRect();
    setClickPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    // Start animation
    setIsAnimating(true);
    setShowOverlay(true);

    // Set CSS variables for overlay position
    document.documentElement.style.setProperty('--x', `${e.clientX}px`);
    document.documentElement.style.setProperty('--y', `${e.clientY}px`);

    // Add transition class to body
    document.body.classList.add('theme-transition');
    document.querySelectorAll('*').forEach(el => {
      el.classList.add('theme-transition');
    });

    // Toggle theme after a tiny delay for animation
    setTimeout(() => {
      toggleTheme();
    }, 50);

    // Remove animation classes
    setTimeout(() => {
      setIsAnimating(false);
      setShowOverlay(false);
      document.body.classList.remove('theme-transition');
      document.querySelectorAll('*').forEach(el => {
        el.classList.remove('theme-transition');
      });
    }, 600);
  };

  // Icon variants for framer-motion
  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    animate: { 
      scale: [1, 1.2, 1],
      rotate: [0, 180, 360],
      transition: { duration: 0.6, ease: "easeInOut" }
    }
  };

  return (
    <>
      {/* Theme transition overlay */}
      <div className={`theme-overlay ${showOverlay ? 'active' : ''}`} />
      
      <motion.button
        onClick={handleThemeToggle}
        className={`relative p-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors ${themeStyles.text} overflow-hidden`}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        whileTap={{ scale: 0.95 }}
      >
        {/* Ripple effect */}
        {isAnimating && (
          <motion.div
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="absolute bg-purple-400 rounded-full"
            style={{
              width: '20px',
              height: '20px',
              left: clickPosition.x - 10,
              top: clickPosition.y - 10,
            }}
          />
        )}

        {/* Animated icons */}
        <AnimatePresence mode="wait">
          {theme === 'dark' ? (
            <motion.div
              key="sun"
              variants={iconVariants}
              initial="initial"
              animate={isAnimating ? "animate" : "initial"}
              className="relative z-10"
            >
              <Sun className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="moon"
              variants={iconVariants}
              initial="initial"
              animate={isAnimating ? "animate" : "initial"}
              className="relative z-10"
            >
              <Moon className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sparkles animation on hover */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100"
          initial={false}
          animate={{ scale: [1, 1.2, 1], opacity: [0, 0.5, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <Sparkles className="w-4 h-4 text-purple-400" />
        </motion.div>
      </motion.button>
    </>
  );
};

export default ThemeToggle;